export const SIGNAL_R_RESOURCE_TYPE = {
    Organization: 'Organization',
    Cluster: 'Cluster',
    TeamChannel: 'TeamChannel',
    Platform: 'Platform'
} as const;

export type SignalRResourceType = typeof SIGNAL_R_RESOURCE_TYPE[keyof typeof SIGNAL_R_RESOURCE_TYPE];

export type SignalRRoomKind = 'resource' | 'personalized';

export interface ISignalRRoomTarget {
    kind: SignalRRoomKind;
    resourceType: string;
    resourceId: string;
}

export interface ISignalRInvokeConnection {
    invoke: (methodName: string, ...args: any[]) => Promise<unknown>;
}

export interface ISignalRRoomMembershipOptions {
    retryDelayMs?: number;
    sleep?: (ms: number) => Promise<void>;
}

const HEADER_PROPAGATION_RETRY_DELAY_MS = 200;

export const isHeaderPropagationHubError = (error: unknown): boolean => {
    const message = error instanceof Error ? error.message : String(error ?? '');
    return /HeaderPropagationValues|header propagation can only be used/i.test(message);
};

const defaultSleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

const connectionTokens = new WeakMap<object, number>();
let nextConnectionToken = 1;

export const signalRRoomHubMethods = (kind: SignalRRoomKind): { connect: string; disconnect: string } => {
    if (kind === 'personalized') {
        return {
            connect: 'ConnectToPersonalizedResource',
            disconnect: 'DisconnectFromPersonalizedResource'
        };
    }

    return {
        connect: 'ConnectToResource',
        disconnect: 'DisconnectFromResource'
    };
};

const organizationUrlNamePattern = /\/orgs\/([^/]+)/;
const clusterIdPattern = /\/cluster\/([^/]+)/;

export const organizationUrlNameFromPath = (pathname: string): string | null => {
    const match = organizationUrlNamePattern.exec(pathname);
    return match?.[1] ? decodeURIComponent(match[1]) : null;
};

export const clusterIdFromPath = (pathname: string): string | null => {
    const match = clusterIdPattern.exec(pathname);
    return match?.[1] ? decodeURIComponent(match[1]) : null;
};

const CLUSTER_ID_QUERY_PARAM = 'clusterId';

export const clusterIdFromSearch = (search: string): string | null => {
    const query = search.startsWith('?') ? search.slice(1) : search;
    const value = new URLSearchParams(query).get(CLUSTER_ID_QUERY_PARAM)?.trim();
    return value || null;
};

export const clusterIdFromLocation = (pathname: string, search: string): string | null =>
    clusterIdFromPath(pathname) ?? clusterIdFromSearch(search);

const connectionToken = (connection: object): number => {
    const existing = connectionTokens.get(connection);
    if (existing) {
        return existing;
    }

    const token = nextConnectionToken;
    nextConnectionToken += 1;
    connectionTokens.set(connection, token);
    return token;
};

const roomKey = (connection: object, target: ISignalRRoomTarget): string =>
    `${connectionToken(connection)}|${target.kind}|${target.resourceType}|${target.resourceId}`;

interface IRoomEntry {
    count: number;
    target: ISignalRRoomTarget;
    connection: ISignalRInvokeConnection;
}

export const createSignalRRoomMembership = (options?: ISignalRRoomMembershipOptions) => {
    const entries = new Map<string, IRoomEntry>();
    const chains = new Map<string, Promise<void>>();
    const connectionChains = new WeakMap<object, Promise<void>>();
    const sleep = options?.sleep ?? defaultSleep;
    const retryDelayMs = options?.retryDelayMs ?? HEADER_PROPAGATION_RETRY_DELAY_MS;

    const enqueue = (key: string, task: () => Promise<void>): void => {
        const previous = chains.get(key) ?? Promise.resolve();
        const next = previous.then(task, task);
        chains.set(key, next.then(() => undefined, () => undefined));
    };

    const enqueueOnConnection = (connection: object, task: () => Promise<void>): Promise<void> => {
        const previous = connectionChains.get(connection) ?? Promise.resolve();
        const next = previous.then(task, task);
        connectionChains.set(connection, next.then(() => undefined, () => undefined));
        return next.then(() => undefined, () => undefined);
    };

    const invokeOnHub = async (connection: ISignalRInvokeConnection, methodName: string, target: ISignalRRoomTarget) => {
        const run = () => connection.invoke(methodName, target.resourceType, target.resourceId);
        try {
            await run();
        } catch (error) {
            // Notifications hub checks access over HTTP with header propagation.
            // WebSocket invokes are not an HTTP request, so the first call can fail
            // and succeed on retry once the connection has settled.
            if (!methodName.startsWith('Connect') || !isHeaderPropagationHubError(error)) {
                return;
            }
            await sleep(retryDelayMs);
            try {
                await run();
            } catch {
                return;
            }
        }
    };

    const invoke = (connection: ISignalRInvokeConnection, methodName: string, target: ISignalRRoomTarget) =>
        enqueueOnConnection(connection, () => invokeOnHub(connection, methodName, target));

    const retain = (connection: ISignalRInvokeConnection, target: ISignalRRoomTarget): (() => void) => {
        if (!target.resourceId) {
            return () => undefined;
        }

        const key = roomKey(connection, target);
        const current = entries.get(key);
        if (current) {
            current.count += 1;
        } else {
            entries.set(key, { count: 1, target, connection });
            const { connect } = signalRRoomHubMethods(target.kind);
            enqueue(key, () => invoke(connection, connect, target));
        }

        let released = false;
        return () => {
            if (released) {
                return;
            }
            released = true;
            const entry = entries.get(key);
            if (!entry) {
                return;
            }
            entry.count -= 1;
            if (entry.count > 0) {
                return;
            }
            entries.delete(key);
            const { disconnect } = signalRRoomHubMethods(target.kind);
            enqueue(key, () => invoke(connection, disconnect, target));
        };
    };

    const rejoin = (connection: ISignalRInvokeConnection): void => {
        entries.forEach((entry, key) => {
            if (entry.connection !== connection || entry.count <= 0) {
                return;
            }
            const { connect } = signalRRoomHubMethods(entry.target.kind);
            enqueue(key, () => invoke(connection, connect, entry.target));
        });
    };

    const whenIdle = async (): Promise<void> => {
        await Promise.all(Array.from(chains.values()));
    };

    return { retain, rejoin, whenIdle };
};

export const signalRRoomMembership = createSignalRRoomMembership();
